import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, MapPin, Tag, Sparkles, ChevronLeft, Shield, HeadphonesIcon, BadgeCheck, Users } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Button } from '../components/ui/button';
import ActivityCard from '../components/ActivityCard';
import { categories, destinations, activities, heroSlides } from '../mock';

const CategoryIcon = ({ name, className }) => {
  const map = { attractions: 'Sparkles', tours: 'Map', transport: 'Train', food: 'UtensilsCrossed', wifi: 'Wifi', rail: 'TrainFront', hotels: 'BedDouble', car: 'Car', wellness: 'Flower2', shows: 'Ticket' };
  const Comp = Icons[map[name] || 'Sparkles'];
  return <Comp className={className} />;
};

export default function Home() {
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, []);

  const bestSellers = activities.slice(0, 8);
  const trending = activities.slice(4, 12);

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[320px] md:h-[440px] overflow-hidden">
          {heroSlides.map((s, i) => (
            <div key={s.id} className={`absolute inset-0 transition-opacity duration-700 ${i === slide ? 'opacity-100' : 'opacity-0'}`}>
              <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 hero-gradient" />
            </div>
          ))}
          <div className="absolute inset-0 container-k flex flex-col justify-end pb-10 md:pb-16">
            <span className="pill text-white mb-3 w-fit" style={{ background: 'rgba(255,91,0,0.95)' }}>
              <Sparkles className="w-3 h-3" /> {heroSlides[slide].tag}
            </span>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white max-w-2xl leading-tight">{heroSlides[slide].title}</h1>
            <p className="text-white/90 text-base md:text-lg mt-2 max-w-xl">{heroSlides[slide].subtitle}</p>
            <div className="mt-4">
              <Button onClick={() => navigate('/search')} className="bg-xlook-orange text-white hover:opacity-90 rounded-full h-11 px-6 text-sm font-semibold">{heroSlides[slide].cta}</Button>
            </div>
          </div>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5">
            {heroSlides.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? 'bg-white w-8' : 'bg-white/60 w-2'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="container-k py-6 md:py-10">
        <div className="grid grid-cols-5 md:grid-cols-10 gap-2 md:gap-3">
          {categories.map((c) => (
            <Link key={c.id} to={`/search?category=${c.id}`} className="cat-tile flex flex-col items-center gap-2 p-2 md:p-3 rounded-xl">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center" style={{ background: '#fff2e8', color: '#ff5b00' }}>
                <CategoryIcon name={c.id} className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <span className="text-[11px] md:text-sm text-gray-800 text-center font-medium">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="container-k py-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs md:text-sm">
          <div className="flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-xlook-orange" /> <span>Best price guaranteed</span></div>
          <div className="flex items-center gap-2"><Users className="w-5 h-5 text-xlook-orange" /> <span>200M+ trips booked</span></div>
          <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-xlook-orange" /> <span>Secure payment</span></div>
          <div className="flex items-center gap-2"><HeadphonesIcon className="w-5 h-5 text-xlook-orange" /> <span>24/7 customer support</span></div>
        </div>
      </section>

      {/* Top Destinations */}
      <section className="container-k py-8 md:py-12">
        <div className="flex items-end justify-between mb-4 md:mb-6">
          <div>
            <h2 className="text-xl md:text-3xl font-extrabold text-gray-900">Top Destinations</h2>
            <p className="text-sm text-gray-500 mt-1">Where locals and travelers love to go</p>
          </div>
          <Link to="/search" className="text-sm text-gray-700 hover:text-xlook-orange flex items-center gap-0.5">See all <ChevronRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {destinations.map((d) => (
            <Link key={d.id} to={`/search?city=${d.id}`} className="activity-card relative aspect-[4/5] rounded-xl overflow-hidden">
              <img src={d.image} alt={d.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white">
                <div className="text-lg md:text-xl font-bold">{d.name}</div>
                <div className="text-xs md:text-sm opacity-90 flex items-center gap-1"><MapPin className="w-3 h-3" /> {d.country}</div>
                <div className="text-[10px] md:text-xs opacity-80 mt-1">{d.activities.toLocaleString()} activities</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Best sellers */}
      <section className="container-k py-6 md:py-10">
        <div className="flex items-end justify-between mb-4 md:mb-6">
          <div>
            <h2 className="text-xl md:text-3xl font-extrabold text-gray-900">Best sellers worldwide</h2>
            <p className="text-sm text-gray-500 mt-1">Activities others are loving right now</p>
          </div>
          <Link to="/search" className="text-sm text-gray-700 hover:text-xlook-orange flex items-center gap-0.5">See all <ChevronRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {bestSellers.map((a) => <ActivityCard key={a.id} activity={a} />)}
        </div>
      </section>

      {/* Deal banner */}
      <section className="container-k py-6 md:py-10">
        <div className="relative rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg,#fff2e8 0%,#ffe4cf 100%)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 items-center">
            <div className="p-6 md:p-10">
              <span className="pill bg-white text-xlook-orange mb-3"><Tag className="w-3 h-3" /> Flash deal</span>
              <h3 className="text-2xl md:text-4xl font-extrabold text-gray-900">Save up to 40% on tickets</h3>
              <p className="text-gray-700 mt-2 max-w-md">Limited-time offers on theme parks, observation decks and top city experiences. Book now, travel later.</p>
              <Button onClick={() => navigate('/search')} className="mt-4 rounded-full bg-xlook-orange text-white hover:opacity-90 h-11 px-6">View deals</Button>
            </div>
            <div className="hidden md:block h-56">
              <img src="https://images.unsplash.com/photo-1502136969935-8d8eef54d77b" alt="Deal" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Trending */}
      <section className="container-k py-6 md:py-10">
        <div className="flex items-end justify-between mb-4 md:mb-6">
          <div>
            <h2 className="text-xl md:text-3xl font-extrabold text-gray-900">Trending this week</h2>
            <p className="text-sm text-gray-500 mt-1">Fresh picks that travelers are booking</p>
          </div>
          <Link to="/search" className="text-sm text-gray-700 hover:text-xlook-orange flex items-center gap-0.5">See all <ChevronRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {trending.map((a) => <ActivityCard key={a.id} activity={a} />)}
        </div>
      </section>

      {/* App promo */}
      <section className="container-k py-8 md:py-12">
        <div className="rounded-2xl bg-gray-900 text-white p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold">Get more out of the Xlook app</h3>
            <p className="text-white/70 mt-2">App-only deals, faster bookings and offline vouchers. Scan to download.</p>
            <div className="flex gap-3 mt-4">
              <a href="#" className="px-4 py-2.5 rounded-lg bg-white text-gray-900 text-sm font-semibold">App Store</a>
              <a href="#" className="px-4 py-2.5 rounded-lg bg-white text-gray-900 text-sm font-semibold">Google Play</a>
            </div>
          </div>
          <div className="flex md:justify-end">
            <div className="w-28 h-28 bg-white rounded-xl flex items-center justify-center">
              <div className="w-24 h-24 bg-[radial-gradient(circle,_#000_1px,_transparent_1px)] bg-[size:6px_6px] rounded-md" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
